import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { DollarSignIcon, TrendingUpIcon, TrendingDownIcon } from 'lucide-react';
import { formatCurrency } from '../../utils/currencyFormatter';
export function BudgetVsActualReport({
  filters,
  locale
}) {
  // Sample data for budget vs actual report
  const budgetData = [{
    department: 'Marketing',
    budget: 150000,
    actual: 142500,
    variance: -7500
  }, {
    department: 'Sales',
    budget: 200000,
    actual: 215000,
    variance: 15000
  }, {
    department: 'IT',
    budget: 300000,
    actual: 285000,
    variance: -15000
  }, {
    department: 'Operations',
    budget: 250000,
    actual: 248000,
    variance: -2000
  }, {
    department: 'HR',
    budget: 100000,
    actual: 98500,
    variance: -1500
  }, {
    department: 'Finance',
    budget: 120000,
    actual: 115000,
    variance: -5000
  }];
  const monthlyData = [{
    month: 'Jan',
    budget: 95000,
    actual: 92000
  }, {
    month: 'Feb',
    budget: 100000,
    actual: 98500
  }, {
    month: 'Mar',
    budget: 110000,
    actual: 115000
  }, {
    month: 'Apr',
    budget: 105000,
    actual: 107000
  }, {
    month: 'May',
    budget: 115000,
    actual: 110000
  }, {
    month: 'Jun',
    budget: 120000,
    actual: 118000
  }];
  const totalBudget = budgetData.reduce((sum, item) => sum + item.budget, 0);
  const totalActual = budgetData.reduce((sum, item) => sum + item.actual, 0);
  const totalVariance = totalActual - totalBudget;
  const variancePercentage = totalVariance / totalBudget * 100;
  return <div className="p-6" data-id="element-16015">
      <div className="mb-8" data-id="element-16016">
        <h3 className="text-lg font-medium text-gray-900" data-id="element-16017">
          Budget vs Actual Spend Report
        </h3>
        <p className="mt-1 text-sm text-gray-500" data-id="element-16018">
          Compare budgeted amounts against actual spend by department, GL code,
          or entity
        </p>
      </div>
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8" data-id="element-16019">
        <div className="bg-white overflow-hidden shadow rounded-lg" data-id="element-16020">
          <div className="p-5" data-id="element-16021">
            <div className="flex items-center" data-id="element-16022">
              <div className="flex-shrink-0 bg-blue-500 rounded-md p-3" data-id="element-16023">
                <DollarSignIcon className="h-6 w-6 text-white" data-id="element-16024" />
              </div>
              <div className="ml-5 w-0 flex-1" data-id="element-16025">
                <dl data-id="element-16026">
                  <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-16027">
                    Total Budget
                  </dt>
                  <dd data-id="element-16028">
                    <div className="text-lg font-medium text-gray-900" data-id="element-16029">
                      {formatCurrency(totalBudget, 'USD', locale)}
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg" data-id="element-16030">
          <div className="p-5" data-id="element-16031">
            <div className="flex items-center" data-id="element-16032">
              <div className="flex-shrink-0 bg-green-500 rounded-md p-3" data-id="element-16033">
                <DollarSignIcon className="h-6 w-6 text-white" data-id="element-16034" />
              </div>
              <div className="ml-5 w-0 flex-1" data-id="element-16035">
                <dl data-id="element-16036">
                  <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-16037">
                    Total Actual
                  </dt>
                  <dd data-id="element-16038">
                    <div className="text-lg font-medium text-gray-900" data-id="element-16039">
                      {formatCurrency(totalActual, 'USD', locale)}
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg" data-id="element-16040">
          <div className="p-5" data-id="element-16041">
            <div className="flex items-center" data-id="element-16042">
              <div className={`flex-shrink-0 ${totalVariance < 0 ? 'bg-green-500' : 'bg-red-500'} rounded-md p-3`} data-id="element-16043">
                {totalVariance < 0 ? <TrendingDownIcon className="h-6 w-6 text-white" data-id="element-16044" /> : <TrendingUpIcon className="h-6 w-6 text-white" data-id="element-16045" />}
              </div>
              <div className="ml-5 w-0 flex-1" data-id="element-16046">
                <dl data-id="element-16047">
                  <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-16048">
                    Variance
                  </dt>
                  <dd data-id="element-16049">
                    <div className="text-lg font-medium text-gray-900" data-id="element-16050">
                      {formatCurrency(totalVariance, 'USD', locale)} (
                      {variancePercentage.toFixed(1)}%)
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Department Chart */}
      <div className="bg-white p-6 rounded-lg shadow mb-8" data-id="element-16051">
        <h4 className="text-base font-medium text-gray-900 mb-4" data-id="element-16052">
          Budget vs Actual by Department
        </h4>
        <div className="h-80" data-id="element-16053">
          <ResponsiveContainer width="100%" height="100%" data-id="element-16054">
            <BarChart data={budgetData} margin={{
            top: 20,
            right: 30,
            left: 20,
            bottom: 5
          }} data-id="element-16055">
              <CartesianGrid strokeDasharray="3 3" data-id="element-16056" />
              <XAxis dataKey="department" data-id="element-16057" />
              <YAxis data-id="element-16058" />
              <Tooltip formatter={value => formatCurrency(value, 'USD', locale)} data-id="element-16059" />
              <Legend data-id="element-16060" />
              <Bar dataKey="budget" name="Budget" fill="#3b82f6" data-id="element-16061" />
              <Bar dataKey="actual" name="Actual" fill="#10b981" data-id="element-16062" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      {/* Monthly Trend */}
      <div className="bg-white p-6 rounded-lg shadow mb-8" data-id="element-16063">
        <h4 className="text-base font-medium text-gray-900 mb-4" data-id="element-16064">
          Monthly Budget vs Actual Trend
        </h4>
        <div className="h-80" data-id="element-16065">
          <ResponsiveContainer width="100%" height="100%" data-id="element-16066">
            <BarChart data={monthlyData} margin={{
            top: 20,
            right: 30,
            left: 20,
            bottom: 5
          }} data-id="element-16067">
              <CartesianGrid strokeDasharray="3 3" data-id="element-16068" />
              <XAxis dataKey="month" data-id="element-16069" />
              <YAxis data-id="element-16070" />
              <Tooltip formatter={value => formatCurrency(value, 'USD', locale)} data-id="element-16071" />
              <Legend data-id="element-16072" />
              <Bar dataKey="budget" name="Budget" fill="#3b82f6" data-id="element-16073" />
              <Bar dataKey="actual" name="Actual" fill="#10b981" data-id="element-16074" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      {/* Department Table */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md" data-id="element-16075">
        <div className="px-4 py-5 border-b border-gray-200 sm:px-6" data-id="element-16076">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-16077">
            Department Budget Details
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500" data-id="element-16078">
            Detailed breakdown by department
          </p>
        </div>
        <div className="overflow-x-auto" data-id="element-16079">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-16080">
            <thead className="bg-gray-50" data-id="element-16081">
              <tr data-id="element-16082">
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-16083">
                  Department
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-16084">
                  Budget
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-16085">
                  Actual
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-16086">
                  Variance
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-16087">
                  % Variance
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-16088">
              {budgetData.map((dept, index) => <tr key={index} data-id="element-16089">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-16090">
                    {dept.department}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right" data-id="element-16091">
                    {formatCurrency(dept.budget, 'USD', locale)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right" data-id="element-16092">
                    {formatCurrency(dept.actual, 'USD', locale)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right" data-id="element-16093">
                    <span className={`${dept.variance < 0 ? 'text-green-600' : 'text-red-600'}`} data-id="element-16094">
                      {formatCurrency(dept.variance, 'USD', locale)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right" data-id="element-16095">
                    <span className={`${dept.variance < 0 ? 'text-green-600' : 'text-red-600'}`} data-id="element-16096">
                      {(dept.variance / dept.budget * 100).toFixed(1)}%
                    </span>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
}